import React from 'react';
import classNames from 'classnames';
import SvgChevron from '@app/shared/icons/Chevron';
import { l } from '@locale/setupI18n';
import styles from '@app/modules/projects/outputs/opm-summary-waterfall/opm-summary-waterfall-erf.module.scss';
import { OpmFirstLastWaterfallDictionaryDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import InstrumentOtherCashFlowMovements from './InstrumentOtherCashFlowMovements';
import InstrumentInterimCashFlowsMinorityDiscounts from './InstrumentInterimCashFlowsMinorityDiscounts';
import InstrumentInfusionsCashFlowMovements from './InstrumentInfusionsCashFlowMovements';
import InstrumentTotalInterimCashFlows from './InstrumentTotalInterimCashFlows';

interface InstrumentInterimCashFlowsProps {
  expanded: { [key: string]: boolean };
  handleToggle: (key: string) => void;
  titleColSpan: number;
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  selectedInstrument: string;
  waterfallIterations: number;
}

const InstrumentInterimCashFlows: React.FC<InstrumentInterimCashFlowsProps> = ({
  expanded,
  handleToggle,
  titleColSpan,
  opmWaterfall,
  selectedYear,
  selectedInstrument,
  waterfallIterations,
}) => {
  const firstSection = opmWaterfall[selectedYear]?.first;
  const firstSimulation = Object.keys(opmWaterfall[selectedYear].first)[0];
  // Get the first item from the dictionary
  const firstSectionFirstItem = firstSection[Object.keys(firstSection)[0]];
  const instrumentData = firstSectionFirstItem
    ? firstSectionFirstItem.instruments[selectedInstrument]
    : undefined;
  const hasInfusions = (instrumentData?.infusionTransactions?.length ?? 0) > 0;
  const hasRedemptions = (instrumentData?.redemptionTransactions?.length ?? 0) > 0;
  const totalCashPay = instrumentData?.transactionSummary?.totalCashPay ?? 0;
  const totalDividends = instrumentData?.transactionSummary?.totalDividends ?? 0;
  const hasCashPay = Boolean(totalCashPay);
  const hasDividends = Boolean(totalDividends);

  const shouldRender = hasInfusions || hasRedemptions || hasCashPay || hasDividends;

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <tr>
        <th
          className={classNames(styles['section-header'], styles['indentation-level-2'])}
          colSpan={titleColSpan}>
          <div className={styles['expand-toggle--indented']}>
            <SvgChevron
              onClick={() => handleToggle('interimCashFlows')}
              className={
                expanded.interimCashFlows ? styles['chevron-expanded'] : styles['chevron-collapsed']
              }
            />
          </div>
          {l('_InterimCashFlows')}
        </th>
      </tr>
      {expanded.interimCashFlows && (
        <>
          <InstrumentInfusionsCashFlowMovements
            titleColSpan={titleColSpan}
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            selectedInstrument={selectedInstrument}
            waterfallIterations={waterfallIterations}
            hasInfusions={hasInfusions}
          />
          <InstrumentOtherCashFlowMovements
            titleColSpan={titleColSpan}
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            selectedInstrument={selectedInstrument}
            waterfallIterations={waterfallIterations}
            hasInfusions={hasInfusions}
            hasRedemptions={hasRedemptions}
            hasCashPay={hasCashPay}
            hasDividends={hasDividends}
          />
          <InstrumentInterimCashFlowsMinorityDiscounts
            titleColSpan={titleColSpan}
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            selectedInstrument={selectedInstrument}
            waterfallIterations={waterfallIterations}
            firstSimulation={firstSimulation}
            hasInfusions={hasInfusions}
          />
          <InstrumentTotalInterimCashFlows
            titleColSpan={titleColSpan}
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            selectedInstrument={selectedInstrument}
            waterfallIterations={waterfallIterations}
          />
        </>
      )}
    </>
  );
};

export default InstrumentInterimCashFlows;
